// useGooseBerryFeedback: encapsulates the per-section feedback state, the
// "Regenerate with my corrections" derivations, and the feedback-submitted
// handler used by the SectionFeedbackBar.
//
// Extracted from GooseBerryView so the parent component shrinks (lower
// cyclomatic complexity) and the feedback subsystem can be tested in isolation.
import { useCallback, useMemo, useState } from "react";

/**
 * @returns {{
 *   feedbackBySection: Record<string, { vote: string, comment: string }>,
 *   handleFeedbackSubmitted: (item: any) => void,
 *   getFeedbackFor: (sectionKey: string) => { vote, comment } | null,
 *   correctionCount: number,
 *   compiledCorrections: Array<{ sectionKey, sectionTitle, comment }>,
 *   resetFeedback: () => void,
 * }}
 */
export function useGooseBerryFeedback() {
  // { [sectionKey]: { vote: "up"|"down", comment: string } }
  const [feedbackBySection, setFeedbackBySection] = useState({});

  const handleFeedbackSubmitted = useCallback((item) => {
    if (!item || !item.sectionKey) return;
    setFeedbackBySection((prev) => ({
      ...prev,
      [item.sectionKey]: { vote: item.vote, comment: item.comment || "" },
    }));
  }, []);

  const getFeedbackFor = useCallback(
    (key) => feedbackBySection[(key || "").toLowerCase()] || null,
    [feedbackBySection]
  );

  const correctionCount = useMemo(
    () =>
      Object.values(feedbackBySection).filter(
        (v) => v && v.vote === "down" && (v.comment || "").trim().length > 0
      ).length,
    [feedbackBySection]
  );

  const compiledCorrections = useMemo(
    () =>
      Object.entries(feedbackBySection)
        .filter(([, v]) => v && v.vote === "down" && (v.comment || "").trim())
        .map(([sectionKey, v]) => ({
          sectionKey,
          sectionTitle: sectionKey,
          comment: v.comment.trim(),
        })),
    [feedbackBySection]
  );

  const resetFeedback = useCallback(() => setFeedbackBySection({}), []);

  return {
    feedbackBySection,
    handleFeedbackSubmitted,
    getFeedbackFor,
    correctionCount,
    compiledCorrections,
    resetFeedback,
  };
}
